import { Box, Button, Flex, Icon, IconButton, Skeleton, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiPlus, FiTrash2 } from "react-icons/fi";
import { Tooltip } from "../components/Tooltip";
import { useAssistantContext } from "./AssistantProvider";
import { ConversationRowDetail } from "./ConversationRowDetail";
import { useConversationActions } from "./useConversationActions";

export const CONVERSATION_LIST_BREAKPOINT = "md";

const LOADING_ROWS = [1, 2, 3];

export const ConversationList = () => {
  const { t } = useTranslation();
  const {
    summaries,
    hasOlderConversations,
    isLoadingOlderConversations,
    showOlderConversations,
    activeConversationId,
    isRunning,
    isLoadingConversations,
    conversationsFailed,
    retryConversations,
  } = useAssistantContext();
  const { startNewConversation, openConversation, confirmDelete, confirmationModal } = useConversationActions();
  const isNewConversation = !summaries.some((conversation) => conversation.id === activeConversationId);
  const deleteLabel = String(t("Delete conversation"));

  return (
    <Flex
      as="nav"
      aria-label={String(t("Conversations"))}
      direction="column"
      w="64"
      flexShrink={0}
      h="full"
      minH="0"
      bg="smBackground"
      borderRight="1px solid"
      borderColor="smBorder"
    >
      <Box p="3" flexShrink={0}>
        <Button
          leftIcon={<FiPlus />}
          size="sm"
          variant={isNewConversation ? "solid" : "outline"}
          colorScheme="teal"
          w="full"
          onClick={startNewConversation}
          isDisabled={isRunning}
          aria-current={isNewConversation ? "true" : undefined}
        >
          {t("New conversation")}
        </Button>
      </Box>
      <Text px="4" pb="1" fontSize="xs" fontWeight="semibold" color="smTextDisabled" flexShrink={0}>
        {t("Conversations")}
      </Text>
      <Stack spacing="0.5" px="2" pb="3" flex="1" minH="0" overflowY="auto">
        {isLoadingConversations && (
          <Stack spacing="3" px="2" py="1" aria-label={String(t("Loading conversations"))}>
            {LOADING_ROWS.map((row) => (
              <Skeleton key={row} h="8" borderRadius="md" />
            ))}
          </Stack>
        )}
        {conversationsFailed && summaries.length === 0 && (
          <Stack spacing="2" px="2" py="1" align="flex-start">
            <Text fontSize="sm" color="smText">
              {t("Could not load your conversations.")}
            </Text>
            <Button size="sm" variant="outline" colorScheme="teal" onClick={() => void retryConversations()}>
              {t("Try again")}
            </Button>
          </Stack>
        )}
        {!isLoadingConversations && !conversationsFailed && summaries.length === 0 && (
          <Text px="2" py="1" fontSize="sm" color="smTextDisabled">
            {t("Your conversations with the assistant are listed here, so you can come back to them.")}
          </Text>
        )}
        {summaries.map((conversation) => {
          const isActive = conversation.id === activeConversationId;
          return (
            <Flex
              key={conversation.id}
              align="center"
              gap="1"
              borderRadius="md"
              bg={isActive ? "smPrimarySofter" : undefined}
              _hover={isActive ? undefined : { bg: "smBackgroundSecondary" }}
            >
              <Box
                as="button"
                type="button"
                flex="1"
                minW="0"
                px="2"
                py="1.5"
                textAlign="left"
                aria-current={isActive ? "true" : undefined}
                disabled={isRunning}
                cursor={isRunning ? "not-allowed" : "pointer"}
                opacity={isRunning && !isActive ? 0.5 : 1}
                onClick={() => openConversation(conversation.id)}
              >
                <Text
                  fontSize="sm"
                  color={isActive ? "smPrimary" : "smText"}
                  fontWeight={isActive ? "medium" : "normal"}
                  isTruncated
                >
                  {conversation.title}
                </Text>
                <ConversationRowDetail conversation={conversation} />
              </Box>
              <Tooltip label={deleteLabel}>
                <IconButton
                  aria-label={deleteLabel}
                  icon={<Icon as={FiTrash2} />}
                  size="xs"
                  variant="ghost"
                  color="smTextDisabled"
                  _hover={{ color: "smError" }}
                  isDisabled={isRunning}
                  onClick={() => confirmDelete(conversation.id, conversation.title)}
                />
              </Tooltip>
            </Flex>
          );
        })}
        {hasOlderConversations && (
          <Button
            size="sm"
            variant="link"
            colorScheme="teal"
            fontWeight="normal"
            alignSelf="flex-start"
            px="2"
            py="1"
            onClick={() => void showOlderConversations()}
            isLoading={isLoadingOlderConversations}
          >
            {t("Show older conversations")}
          </Button>
        )}
      </Stack>
      {confirmationModal}
    </Flex>
  );
};
